/** Filter bar + Display menu for issue lists (Linear's top-right controls). */
import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { useTranslation } from "react-i18next";
import { LayoutList, LayoutGrid, ListFilter, SlidersHorizontal, X } from "lucide-react";
import { PRIORITIES } from "@/lib/issues";
import { liveLabels, liveStates, type ProjectData } from "@/lib/store";
import { useViews, type Filters, type ViewSettings } from "@/lib/views";
import { Avatar, LabelDot, PriorityIcon, StateIcon } from "./PropertyIcons";
import { Picker } from "./Picker";

export function ViewControls({ d, viewKey, view }: { d: ProjectData; viewKey: string; view: ViewSettings }) {
  const setView = useViews((s) => s.set);
  const [filterOpen, setFilterOpen] = useState(false);

  return (
    <div className="flex shrink-0 items-center gap-2">
      <FilterPicker d={d} view={view} viewKey={viewKey} open={filterOpen} onOpenChange={setFilterOpen} />
      <DisplayMenu view={view} onChange={(p) => setView(viewKey, p)} />
    </div>
  );
}

export function countFilters(f: Filters) {
  return (f.states?.length ? 1 : 0) + (f.assignees?.length ? 1 : 0) + (f.priorities?.length ? 1 : 0) + (f.labels?.length ? 1 : 0) + (f.text ? 1 : 0);
}

/** Active filter chips shown under the tabs. */
export function FilterChips({ d, view, viewKey }: { d: ProjectData; view: ViewSettings; viewKey: string }) {
  const { t } = useTranslation();
  const setFilters = useViews((s) => s.setFilters);
  const f = view.filters;
  const chips: { key: keyof Filters; label: string }[] = [];
  if (f.states?.length) chips.push({ key: "states", label: `${t("issue.status")}: ${f.states.map((s) => d.states[s]?.name).join(", ")}` });
  if (f.priorities?.length) chips.push({ key: "priorities", label: `${t("issue.priority")}: ${f.priorities.map((p) => t(`priority.${PRIORITIES[p]?.key}`)).join(", ")}` });
  if (f.assignees?.length) chips.push({ key: "assignees", label: `${t("issue.assignee")}: ${f.assignees.map((a) => (a ? d.users[a]?.name : t("issue.unassigned"))).join(", ")}` });
  if (f.labels?.length) chips.push({ key: "labels", label: `${t("issue.labels")}: ${f.labels.map((l) => d.labels[l]?.name).join(", ")}` });
  if (f.text) chips.push({ key: "text", label: `"${f.text}"` });
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-sidebar/30 px-4 py-2.5">
      {chips.map((c) => (
        <span key={c.key} className="inline-flex h-7 min-w-0 max-w-full items-center gap-1 rounded-md border border-border bg-elevated py-0.5 pl-2.5 pr-1 text-[12px]">
          <span className="truncate" title={c.label}>{c.label}</span>
          <button className="shrink-0 rounded p-1 text-muted hover:bg-hover hover:text-fg" aria-label={`${t("view.clearFilters")}: ${c.label}`} onClick={() => setFilters(viewKey, { ...f, [c.key]: undefined })}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <button className="rounded px-1 py-1 text-[12px] text-muted hover:bg-hover hover:text-fg" onClick={() => setFilters(viewKey, {})}>
        {t("view.clearFilters")}
      </button>
    </div>
  );
}

function FilterPicker({ d, view, viewKey, open, onOpenChange }: { d: ProjectData; view: ViewSettings; viewKey: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const setFilters = useViews((s) => s.setFilters);
  const f = view.filters;
  const activeFilters = countFilters(f);
  const toggle = <K extends keyof Filters>(key: K, val: NonNullable<Filters[K]>[number]) => {
    const cur = (f[key] as unknown[] | undefined) ?? [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    setFilters(viewKey, { ...f, [key]: next.length ? next : undefined });
  };
  const items = [
    ...liveStates(d).map((s) => ({ id: `state:${s.id}`, label: s.name, icon: <StateIcon state={s} />, hint: t("issue.status") })),
    ...PRIORITIES.map((p) => ({ id: `prio:${p.value}`, label: t(`priority.${p.key}`), icon: <PriorityIcon priority={p.value} />, hint: t("issue.priority") })),
    { id: "assignee:none", label: t("issue.unassigned"), icon: <Avatar size={16} />, hint: t("issue.assignee") },
    ...Object.values(d.users).map((u) => ({ id: `assignee:${u.id}`, label: u.name, icon: <Avatar user={u} size={16} />, hint: t("issue.assignee") })),
    ...liveLabels(d).map((l) => ({ id: `label:${l.id}`, label: l.name, icon: <LabelDot color={l.color} />, hint: t("issue.labels") })),
  ];
  const value = [
    ...(f.states ?? []).map((s) => `state:${s}`),
    ...(f.priorities ?? []).map((p) => `prio:${p}`),
    ...(f.assignees ?? []).map((a) => `assignee:${a ?? "none"}`),
    ...(f.labels ?? []).map((l) => `label:${l}`),
  ];
  return (
    <Picker
      open={open}
      onOpenChange={onOpenChange}
      multi
      value={value}
      items={items}
      placeholder={t("view.filterPlaceholder")}
      trigger={
        <button
          className={`btn h-8 min-w-8 gap-1 rounded-full px-2 text-[12px] ${activeFilters ? "border-accent/50 bg-accent/10 text-fg" : "text-muted"}`}
          title={t("view.filter")}
          aria-label={t("view.filter")}
        >
          <ListFilter className="h-3.5 w-3.5" />
          {activeFilters > 0 && <span className="flex h-4 min-w-4 items-center justify-center rounded bg-accent px-1 text-[10px] font-medium tabular-nums text-accent-fg">{activeFilters}</span>}
        </button>
      }
      onSelect={(id) => {
        const [kind, v] = id.split(":") as [string, string];
        if (kind === "state") toggle("states", v);
        if (kind === "prio") toggle("priorities", Number(v));
        if (kind === "assignee") toggle("assignees", v === "none" ? null : v);
        if (kind === "label") toggle("labels", v);
      }}
    />
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 text-[12px]">
      <span className="min-w-0 text-muted">{label}</span>
      {children}
    </label>
  );
}

function Select<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <select className="input h-8 w-[132px] shrink-0 py-0 text-[12px]" value={value} onChange={(e) => onChange(e.target.value as T)}>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

function DisplayMenu({ view, onChange }: { view: ViewSettings; onChange: (p: Partial<ViewSettings>) => void }) {
  const { t } = useTranslation();
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button className="btn h-8 w-8 rounded-full p-0 text-muted" title={t("view.display")} aria-label={t("view.display")}>
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8} collisionPadding={12} className="z-50 max-h-[var(--radix-popover-content-available-height)] w-[312px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-xl border border-border bg-elevated p-3 shadow-xl">
          <div className="mb-3 flex gap-1 rounded-lg border border-border bg-sidebar p-1">
            {(["list", "board"] as const).map((l) => (
              <button key={l} aria-pressed={view.layout === l} className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-2 text-[12px] font-medium transition-colors ${view.layout === l ? "bg-elevated text-fg shadow-sm" : "text-muted hover:bg-hover hover:text-fg"}`} onClick={() => onChange({ layout: l })}>
                {l === "list" ? <LayoutList className="h-3.5 w-3.5" /> : <LayoutGrid className="h-3.5 w-3.5" />} {t(`view.${l}`)}
              </button>
            ))}
          </div>
          <Row label={t("view.groupBy")}>
            <Select value={view.groupBy} onChange={(v) => onChange({ groupBy: v })} options={[["state", t("issue.status")], ["assignee", t("issue.assignee")], ["priority", t("issue.priority")], ["label", t("issue.labels")], ["task", t("issue.task")], ["none", t("view.noGrouping")]]} />
          </Row>
          <Row label={t("view.orderBy")}>
            <Select value={view.orderBy} onChange={(v) => onChange({ orderBy: v })} options={[["manual", t("view.manual")], ["priority", t("issue.priority")], ["updated", t("view.updated")], ["created", t("view.created")], ["due", t("issue.dueDate")]]} />
          </Row>
          <Row label={t("view.showCompleted")}>
            <Select value={view.showCompleted} onChange={(v) => onChange({ showCompleted: v })} options={[["none", t("view.completedNone")], ["week", t("view.completedWeek")], ["month", t("view.completedMonth")], ["all", t("view.completedAll")]]} />
          </Row>
          <label className="flex cursor-pointer items-center justify-between gap-3 py-2 text-[12px]">
            <span className="text-muted">{t("view.showSubIssues")}</span>
            <input type="checkbox" className="h-3.5 w-3.5 accent-accent" checked={view.showSubIssues} onChange={(e) => onChange({ showSubIssues: e.target.checked })} />
          </label>
          <label className="flex cursor-pointer items-center justify-between gap-3 py-2 text-[12px]">
            <span className="text-muted">{t("view.showEmptyGroups")}</span>
            <input type="checkbox" className="h-3.5 w-3.5 accent-accent" checked={view.showEmptyGroups} onChange={(e) => onChange({ showEmptyGroups: e.target.checked })} />
          </label>
          <div className="mt-2 border-t border-border pt-3">
            <div className="mb-2 text-[11px] font-medium text-muted">{t("view.properties")}</div>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(view.props) as (keyof ViewSettings["props"])[]).map((k) => (
                <button key={k} aria-pressed={view.props[k]} className={`rounded-md border px-2 py-1 text-[12px] transition-colors ${view.props[k] ? "border-accent/50 bg-accent/10 text-fg" : "border-border text-muted hover:bg-hover hover:text-fg"}`} onClick={() => onChange({ props: { ...view.props, [k]: !view.props[k] } })}>
                  {t(`view.prop.${k}`)}
                </button>
              ))}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
